import {
	AccountInfo,
	AuthenticationResult,
	AuthError,
	BrowserAuthError,
	EndSessionRequest,
	PublicClientApplication,
	RedirectRequest,
	InteractionRequiredAuthError,
} from '@azure/msal-browser'
import { getVerificationStatusFromToken, redirectToVerificationPage, getRegionRedirect } from './verify-token'
import { isAbsoluteUrl } from '@gbds/core/src/ts/utils'
import {
	AuthEvents,
	AuthState,
	AuthStorageKeys,
	clearUserInfo,
	verificationConstants,
} from '../../config/auth-enumerations'
import { ClientApplication } from '../../models/client-application'
import { InactivityEvents, InactivityStorageKeys, InactivityTracker } from '../inactivity-tracker'
import { CREATE_ACCOUNT_URL, API_LOGOUT_CLEAR_COOKIE_PATH } from '../../config/paths'
import { FinalAuthConfig } from '../../config/auth-config'
import {
	getGmAccountsCdnEntraAuthBaseUrl,
	getGmAccountsCdnEntraLogoutBaseUrl,
} from '../../config/entra-gm-accounts-cdn-login'
import {
	countryDataAttribute,
	environmentDataAttribute,
	languageDataAttribute,
	useDataAttributes,
} from '../../config/script-attributes'
import { isSilentSsoAuthConfigComplete } from '../../config/silent-sso-auth-config'
import { getRelayTokenWaypointAbsoluteUrl } from '../../config/waypoint-utils'
import { URLConfig } from '../../components/global-nav/configs/base'
import { error, log, warn } from './auth-only-logger'
import { dispatchGbpeAuthStateUpdated } from './dispatch-gbpe-auth-state-updated'

export class StandardClientApplication implements ClientApplication {
	//Private members
	protected _clientId: string
	private _clientApp: PublicClientApplication
	/** MSAL-registered redirect (auth-waypoint); never use arbitrary page URLs for silent/redirect flows */
	private readonly _registeredRedirectUri: string
	/** Default post-logout landing URL from MSAL config (used for Entra CDN RP-initiated logout query param). */
	private readonly _postLogoutRedirectUri: string
	private _redirectRequest: RedirectRequest
	private _signupRequest: RedirectRequest
	protected _accountInfo: AccountInfo
	private _state: string
	private _authState: number = AuthState.loading
	private _accessExpiration: Date
	private _authenticated: boolean
	private _timer: InactivityTracker
	private _myAccountURL: string
	private _createAccountURL: string = window.location.origin + CREATE_ACCOUNT_URL
	private _boundStorageHandler: (event: StorageEvent) => void
	private _boundInactiveUserHandler: () => void
	private _locale: string
	private _region: string
	private _loginHint: string
	/** False when auth config is incomplete; used to skip MSAL ssoSilent (SDPBSD-12012). */
	private readonly _silentSsoAuthConfigComplete: boolean
	private _exceptionThrown: boolean
	private _instanceId: string // Unique ID to track this instance

	protected _idToken: string
	protected _accessToken: string

	// Static counter to track total instances created
	private static _instanceCounter = 0

	//Constructor
	constructor(config: FinalAuthConfig, private _urlConfig: URLConfig, required = false) {
		// Generate unique instance ID
		StandardClientApplication._instanceCounter++
		this._instanceId = `SCA-${StandardClientApplication._instanceCounter}-${Date.now()}`
		log(`[DEBUG Constructor] Creating instance ${this._instanceId} on ${window.location.pathname}`)
		this._clientApp = new PublicClientApplication(config.msalConfig)
		this._clientId = config.msalConfig.auth.clientId
		this._registeredRedirectUri = config.redirectRequest.redirectUri
		this._postLogoutRedirectUri = config.msalConfig.auth.postLogoutRedirectUri || window.location.origin
		this._redirectRequest = config.redirectRequest
		this._signupRequest = config.signUpRequest
		this._locale = config.locale
		this._region = config.region
		this._loginHint = config.loginHint
		this._silentSsoAuthConfigComplete = isSilentSsoAuthConfigComplete({
			clientId: config.msalConfig.auth.clientId,
			authority: config.msalConfig.auth.authority,
			useDataAttributes,
			country: countryDataAttribute,
			language: languageDataAttribute,
			environment: environmentDataAttribute,
		})
		this._exceptionThrown = false
		//TODO: Have to talk with team on how we are storing state in terms of missing info
		this._state = sessionStorage.getItem(AuthStorageKeys.state)
			? sessionStorage.getItem(AuthStorageKeys.state)
			: config.redirectRequest.state.split('|')[0]
		this._initializeAsync(required)
	}

	/**
	 * Async initialization - MSAL 2.15+ requires initialize() before any operations
	 */
	private async _initializeAsync(required: boolean): Promise<void> {
		try {
			// Identify which page we're on
			const isWaypoint = window.location.pathname.includes('auth-waypoint')
			const pageType = isWaypoint ? 'AUTH-WAYPOINT' : 'BRAND-SITE'
			log(`[DEBUG _initializeAsync] [${this._instanceId}] [${pageType}] Starting MSAL initialization`)
			log(`[DEBUG _initializeAsync] [${pageType}] URL: ${window.location.href}`)

			// Log accounts BEFORE initialize
			const accountsBefore = this._clientApp.getAllAccounts()
			log(`[DEBUG _initializeAsync] [${pageType}] Accounts BEFORE initialize(): ${accountsBefore.length}`)

			// Log relevant localStorage keys BEFORE initialize
			const msalKeysBefore = Object.keys(localStorage).filter(
				k => k.includes('msal') || k.includes(this._clientId)
			)
			log(`[DEBUG _initializeAsync] [${pageType}] localStorage keys BEFORE: ${msalKeysBefore.length}`)

			await this._clientApp.initialize()

			// Log accounts AFTER initialize
			const accountsAfter = this._clientApp.getAllAccounts()
			log(`[DEBUG _initializeAsync] [${pageType}] Accounts AFTER initialize(): ${accountsAfter.length}`)

			// Log relevant localStorage keys AFTER initialize
			const msalKeysAfter = Object.keys(localStorage).filter(
				k => k.includes('msal') || k.includes(this._clientId)
			)
			log(`[DEBUG _initializeAsync] [${pageType}] localStorage keys AFTER: ${msalKeysAfter.length}`)

			// Check if accounts changed
			if (accountsBefore.length !== accountsAfter.length) {
				warn(
					`[DEBUG _initializeAsync] [${pageType}] ACCOUNT COUNT CHANGED! ` +
						`Before: ${accountsBefore.length}, After: ${accountsAfter.length}`
				)
			}

			log(`[DEBUG _initializeAsync] [${pageType}] MSAL initialized, calling _handleRedirectPromise`)
			await this._handleRedirectPromise(required)
		} catch (err) {
			error('[_initializeAsync] Failed to initialize MSAL:', err)
			this._handleAuthError(err as AuthError)
		}
	}

	//Protected Functions

	//handle auth result is immediately after logging in,
	//while else is every other load page load
	protected async _handleRedirectPromise(required: boolean): Promise<void> {
		const isWaypoint = window.location.pathname.includes('auth-waypoint')
		const pageType = isWaypoint ? 'AUTH-WAYPOINT' : 'BRAND-SITE'
		const hasHashResponse = window.location.hash.includes('code=') || window.location.hash.includes('error=')
		log(`[DEBUG _handleRedirectPromise] [${pageType}] Starting, hasHash=${hasHashResponse}`)
		try {
			const res = await this._clientApp.handleRedirectPromise()
			log(
				`[DEBUG _handleRedirectPromise] handleRedirectPromise resolved, res: ${
					res ? 'AuthResult present' : 'null'
				}`
			)
			// If we're coming back from a redirect, handle it,
			// otherwise run select account. `res` is null when
			// not coming back from a redirect.
			if (res) {
				log('[DEBUG _handleRedirectPromise] Calling _handleAuthResult')
				await this._handleAuthResult(res, required)
			} else {
				log('[DEBUG _handleRedirectPromise] Calling _selectAccount')
				await this._selectAccount(required)
			}
		} catch (err) {
			this._handleAuthError(err as AuthError)
		} finally {
			if (this._exceptionThrown === true) {
				// DEBUG: Log when logout redirect would have happened
				error(
					`[_handleRedirectPromise] EXCEPTION WAS THROWN - would have redirected to logout! ` +
						`logoutURL=${this._urlConfig.logoutURL}, currentURL=${window.location.href}`
				)
				// TEMPORARILY DISABLED: Don't redirect to logout on errors - this may be invalidating B2C sessions
				// TODO: Re-evaluate if this redirect is needed and under what specific conditions
				warn('[_handleRedirectPromise] Logout redirect DISABLED for debugging - proceeding without redirect')
				// location.replace(this._urlConfig.logoutURL)
			}
		}
	}

	/**
	 * Function to retry a call to an API.  Returns true if the call succeeds and false otherwise.
	 * @param callpath - The path to the API call being made.
	 * @param tries - The number of attempts to retry the call.
	 * @param waitTime - The time in milliseconds to wait between each try.
	 * @param error - An error to be thrown if an error is caught from the call.
	 * @param callParams - (Optional) An object containing the parameters sent with the call.
	 * @param failedMessage - (Optional) A message that is output to the console upon call failure.
	 */
	protected async _retryAPICall(
		callPath: string,
		tries: number,
		waitTime: number,
		error: Error,
		callParams?,
		failedMessage?: string
	): Promise<boolean> {
		let callLoopCount = 0
		let responseStatus

		while (callLoopCount < tries) {
			try {
				const fetch_params = callParams ?? {}
				const response = await fetch(callPath, fetch_params)
				log(`Response Status ${response.status}`)
				responseStatus = response.status
				callLoopCount++
			} catch {
				throw error
			} finally {
				if (responseStatus === 200) {
					//Call Succeeded
					// eslint-disable-next-line no-unsafe-finally
					return true
				} else {
					if (failedMessage) {
						log(failedMessage + ' (' + responseStatus + ')')
					}

					//Break instead of waiting after the final try.
					if (callLoopCount === tries) {
						// eslint-disable-next-line no-unsafe-finally
						break
					}

					//Wait and then retry the call
					await new Promise(resolve => setTimeout(resolve, waitTime))
				}
			}
		}
		//Call Failed
		return false
	}

	protected _setAuthenticated(expiration: Date) {
		log(`[DEBUG _setAuthenticated] Setting auth state to AUTHENTICATED, expiration: ${expiration.toISOString()}`)
		this._accessExpiration = expiration
		this._authState = AuthState.authenticated
		dispatchGbpeAuthStateUpdated()
		this._dispatchAuthChecked()

		//Here is where we get the tracker for inactivity
		const customTimeout = sessionStorage.getItem(InactivityStorageKeys.customTimeOut)
		//We first need to get the custom timeout in session storage, determine if it's valid
		//and we need to make sure we don't leave a vulnerability by allowing more than default time for inactivity
		const timeLimit =
			customTimeout && !isNaN(parseInt(customTimeout)) && parseInt(customTimeout) <= 15 * 60 * 1000
				? parseInt(customTimeout)
				: 15 * 60 * 1000 // 15 Minutes
		this._timer = new InactivityTracker(timeLimit)

		//Bind and add the inactive user function to the inactive user event listener
		this._boundInactiveUserHandler = this._handleInactiveUser.bind(this)
		window.addEventListener(InactivityEvents.inactiveUser, this._boundInactiveUserHandler)

		//Bind and add the storage handler function to the storage event listener
		this._boundStorageHandler = this._handleStorageEvent.bind(this)
		window.addEventListener('storage', this._boundStorageHandler)
	}

	//Private Functions

	private async _handleAuthResult(response: AuthenticationResult, required: boolean): Promise<void> {
		log(`[DEBUG _handleAuthResult] account: ${response.account?.username}, fromCache: ${response.fromCache}`)
		log(`[DEBUG _handleAuthResult] idToken: ${!!response.idToken}, accessToken: ${!!response.accessToken}`)
		this._accessToken = response.accessToken !== '' ? response.accessToken : undefined
		this._idToken = response.idToken
		log('[DEBUG _handleAuthResult] Tokens stored, now calling _selectAccount')
		await this._selectAccount(required)
		log('[DEBUG _handleAuthResult] _selectAccount completed')
		const emailVerifiedProperty = getVerificationStatusFromToken(this._idToken)
		const emailVerified =
			emailVerifiedProperty && emailVerifiedProperty.toLowerCase() === verificationConstants.verified
		//if the email is verified, the user should be redirected off of waypoint
		//Updating for now because Europe Token does not have verification flag
		//TODO: Discuss with My Account what the plan is for Europe Token
		if (emailVerified || this._region.toUpperCase() === 'CH' || this._region.toUpperCase() === 'SE') {
			//Find the appended locale delimiter without truncating pipes in the return URL
			const localeIndex = response.state.lastIndexOf('|')

			//if the locale exists, only go to the url portion of the state param
			//Else, just use the entire state param
			if (localeIndex > -1) {
				this._redirectToState(response.state.substr(0, localeIndex))
			} else {
				this._redirectToState(response.state)
			}
		}
	}

	private _handleAuthError(authError: AuthError) {
		// Need to reset redirect uri's when returning from Azure with an error.
		// This is because Azure adds stuff to the url when returning with an error.
		this._resetRedirectURIs()
		this._exceptionThrown = true

		// DEBUG: Log when exception flag is set - this will trigger logout redirect!
		error(
			`[_handleAuthError] EXCEPTION THROWN - setting _exceptionThrown=true. ` +
				`errorCode=${authError?.errorCode}, errorMessage=${authError?.message}, name=${authError?.name}`
		)

		log('Error occurred in standard application', authError)

		// Terminal failure: avoid leaving _authState at loading (0) while gbpe-authLoaded has fired.
		this._setUnauthenticated()
	}

	private _resetRedirectURIs(RedirectURI?: string) {
		// After Azure errors, MSAL may leave a polluted redirectUri. Reset to the app-registered
		// waypoint to prevent AADB2C90006 when _handleAuthError ran on a normal page).
		this._redirectRequest.redirectUri = RedirectURI ?? this._registeredRedirectUri
	}

	private async _selectAccount(required: boolean): Promise<void> {
		const currentAccounts = this._clientApp.getAllAccounts()
		log(`[DEBUG _selectAccount] getAllAccounts returned: ${currentAccounts.length} accounts`)

		// First, check if there's an active account set
		const activeAccount = this._clientApp.getActiveAccount()
		if (activeAccount) {
			log(`[DEBUG _selectAccount] Found active account: ${activeAccount.username}`)

			// Validate the active account - check if its token is expired
			const claims = activeAccount.idTokenClaims as Record<string, unknown>
			const exp = claims?.exp ? Number(claims.exp) : 0
			const now = Date.now() / 1000
			const isExpired = exp > 0 && exp < now

			if (isExpired && currentAccounts.length > 1) {
				// Active account is expired but there are other accounts - check if any are fresher
				warn(
					`[DEBUG _selectAccount] Active account EXPIRED (exp=${new Date(
						exp * 1000
					).toISOString()}), checking ${currentAccounts.length} accounts for fresher one`
				)
				this._handleMultipleAccounts(required)
				return
			}

			// Active account is valid OR it's the only account - use it
			await this._handleSingleAccount(activeAccount, required)
			return
		}

		// No active account - fall back to account selection logic
		if (currentAccounts.length === 0) {
			await this._handleNoAccounts(required)
			return
		} else if (currentAccounts.length > 1) {
			log(`[DEBUG _selectAccount] No active account, multiple accounts found`)
			this._handleMultipleAccounts(required)
		} else if (currentAccounts.length === 1) {
			log(`[DEBUG _selectAccount] No active account, using single account`)
			await this._handleSingleAccount(currentAccounts[0], required)
		}
	}

	/**
	 * Contexts where we skip MSAL {@link PublicClientApplication#ssoSilent} (same idea as excluding ko-KR).
	 */
	private _isSsoSilentExcludedPath(): boolean {
		const path = window.location.pathname
		if (path === '/supportrequest' || path.startsWith('/supportrequest/')) {
			return true
		}
		if (path === '/ownercenter' || path.startsWith('/ownercenter/')) {
			return true
		}
		if (path === '/myaccount' || path.startsWith('/myaccount/')) {
			return true
		}
		return Boolean(sessionStorage.getItem(AuthStorageKeys.externalIdToken))
	}

	private async _handleNoAccounts(required: boolean) {
		// Debug: Check what MSAL keys exist in localStorage and sessionStorage
		const isOnIdpHostedDomain =
			window.location.hostname.includes('custlogin') ||
			window.location.hostname.includes('b2clogin') ||
			window.location.hostname.includes('ciamlogin')
		const msalLocalKeys = Object.keys(localStorage).filter(k => k.includes('msal') || k.includes(this._clientId))
		const msalSessionKeys = Object.keys(sessionStorage).filter(
			k => k.includes('msal') || k.includes(this._clientId)
		)
		log(`[DEBUG _handleNoAccounts] MSAL localStorage keys: ${msalLocalKeys.join(', ')}`)
		log(`[DEBUG _handleNoAccounts] MSAL sessionStorage keys: ${msalSessionKeys.join(', ')}`)

		// Check for any interaction in progress
		const interactionStatus = sessionStorage.getItem(`msal.${this._clientId}.interaction.status`)
		if (interactionStatus) {
			warn(`[DEBUG _handleNoAccounts] Interaction in progress: ${interactionStatus}`)
		}

		const redirect_after_logout = sessionStorage.getItem(InactivityStorageKeys.userSignedOut)
		if (redirect_after_logout && redirect_after_logout === 'true') {
			sessionStorage.removeItem(InactivityStorageKeys.userSignedOut)
			log('User has been signed out due to inactivity')
		}
		const otherSsoSilentPreconditionsMet =
			this._locale !== 'ko-KR' && !isOnIdpHostedDomain && !this._isSsoSilentExcludedPath()
		const canAttemptSsoSilent = otherSsoSilentPreconditionsMet && this._silentSsoAuthConfigComplete

		if (otherSsoSilentPreconditionsMet && !this._silentSsoAuthConfigComplete) {
			log(
				'[ssoSilent] Skipped - incomplete auth configuration (missing clientId, authority, or required data attributes)'
			)
		}

		if (canAttemptSsoSilent) {
			// Attempt silent SSO to detect existing B2C session with timeout
			try {
				log(`[ssoSilent] Attempting with redirectUri: ${this._redirectRequest.redirectUri}`)

				// Create a timeout promise to prevent ssoSilent from hanging indefinitely
				const SSO_TIMEOUT_MS = 8000
				const timeoutPromise = new Promise<never>((_, reject) => {
					setTimeout(() => reject(new Error('ssoSilent_timeout')), SSO_TIMEOUT_MS)
				})

				const ssoPromise = this._clientApp.ssoSilent({
					scopes: this._redirectRequest.scopes,
					loginHint: this._loginHint || undefined,
					state: this._redirectRequest.state,
					redirectUri: this._redirectRequest.redirectUri,
				})

				const ssoResult = await Promise.race([ssoPromise, timeoutPromise])
				// SSO succeeded - B2C session exists
				log(`[ssoSilent] Success! User: ${ssoResult.account?.name}`)
				this._handleAuthResult(ssoResult, required)
				return
			} catch (err) {
				const errorMessage = err instanceof Error ? err.message : String(err)
				const errorCode = (err as { errorCode?: string })?.errorCode || ''
				const subError = (err as { subError?: string })?.subError || ''
				const correlationId = (err as { correlationId?: string })?.correlationId || ''

				// Log full error details for debugging
				warn(
					`[ssoSilent] Failed - errorCode=${errorCode}, subError=${subError}, ` +
						`correlationId=${correlationId}, message=${errorMessage}`
				)

				// Handle known ssoSilent failure scenarios gracefully
				if (err instanceof InteractionRequiredAuthError) {
					log('[ssoSilent] InteractionRequiredAuthError - No B2C session found or user must re-authenticate')
					log(`[ssoSilent] This means B2C has no session cookie for this user on this browser`)
				} else if (errorMessage === 'ssoSilent_timeout') {
					warn('[ssoSilent] Timed out after 8 seconds - proceeding as unauthenticated')
				} else if (errorCode === 'state_not_found' || errorMessage.includes('state_not_found')) {
					warn('[ssoSilent] State mismatch (possibly stale) - proceeding as unauthenticated')
				} else if (errorCode === 'monitor_window_timeout' || errorMessage.includes('monitor_window_timeout')) {
					warn('[ssoSilent] Iframe timed out - proceeding as unauthenticated')
				} else {
					warn('[ssoSilent] Failed:', err)
				}
			}
		}
		this._checkAuthLocalStorage()
		//If there's an external login indicator or if auth is required , we expect to be in the logged in state
		const forcedLoginIndicator = sessionStorage.getItem(AuthStorageKeys.forceLoginIndicator)

		//Check to see if login has been prompted or not here
		const promptLogin = sessionStorage.getItem(AuthStorageKeys.promptLogin) === 'true'

		if (required || (forcedLoginIndicator && forcedLoginIndicator === 'true')) {
			sessionStorage.removeItem(AuthStorageKeys.forceLoginIndicator)
			//User should be logged in to the page they are viewing at the moment
			//Else, redirect back to the origin
			if (required) {
				this.loginRedirect(window.location.href, undefined, promptLogin)
			} else {
				this.loginRedirect(window.location.origin, undefined, promptLogin)
			}
		}
	}

	/**
	 * Method occurs when there are multiple accounts cached but no active account is set.
	 * This is a fallback for edge cases - normally getActiveAccount() should return the correct one.
	 * @param required - boolean used to determine if authentication is required to view page
	 * @private
	 */
	private _handleMultipleAccounts(required: boolean) {
		const allAccounts = this._clientApp.getAllAccounts()
		warn(`[_handleMultipleAccounts] ${allAccounts.length} accounts but no active account set`)

		// Log info about each account for debugging
		allAccounts.forEach((account, index) => {
			const claims = account.idTokenClaims as Record<string, unknown>
			const exp = claims?.exp ? new Date(Number(claims.exp) * 1000).toISOString() : 'unknown'
			const iat = claims?.iat ? new Date(Number(claims.iat) * 1000).toISOString() : 'unknown'
			log(`[_handleMultipleAccounts] Account ${index}: ${account.username}, exp=${exp}, iat=${iat}`)
		})

		// Find the most recently issued NON-EXPIRED account
		const now = Date.now() / 1000
		let bestAccount = allAccounts[0]
		let bestIat = 0

		for (const account of allAccounts) {
			const claims = account.idTokenClaims as Record<string, unknown>
			const iat = claims?.iat ? Number(claims.iat) : 0
			const exp = claims?.exp ? Number(claims.exp) : 0

			// Skip expired accounts
			if (exp > 0 && exp < now) {
				warn(`[_handleMultipleAccounts] Skipping expired account: ${account.username}`)
				continue
			}

			if (iat > bestIat) {
				bestIat = iat
				bestAccount = account
			}
		}

		log(`[_handleMultipleAccounts] Selected account: ${bestAccount.username}`)
		this._handleSingleAccount(bestAccount, required)
	}

	private async _handleSingleAccount(currentAccount: AccountInfo, required: boolean) {
		log(`[DEBUG _handleSingleAccount] Starting with account: ${currentAccount.username}`)
		this._accountInfo = currentAccount
		//sets public accessors for MSAl clientApp object
		this._clientApp.setActiveAccount(this._accountInfo)

		// Date/time in which the access and ID tokens expire
		const expirationSeconds = Number((this._accountInfo.idTokenClaims as Record<string, unknown>).exp)
		const expirationDate = new Date(expirationSeconds * 1000) //Converting to milliseconds for proper conversion to Date object
		log(`[DEBUG _handleSingleAccount] Token expiration: ${expirationDate.toISOString()}`)

		// Check if we already have valid tokens (e.g., from redirect or ssoSilent)
		const tokenExpired = this._tokenExpired(expirationDate)
		log(`[DEBUG _handleSingleAccount] tokenExpired=${tokenExpired}, hasIdToken=${!!this._idToken}`)

		// Skip acquireTokenSilent if we already have a valid, non-expired token
		if (this._idToken && !tokenExpired) {
			log('[DEBUG _handleSingleAccount] Already have valid token, skipping acquireTokenSilent')
			this._setAuthenticated(expirationDate)
			this._handleUnverifiedUser()
			return
		}

		// Use MSAL's acquireTokenSilent API for reliable token retrieval (handles cache key format internally)
		log('[DEBUG _handleSingleAccount] Calling acquireTokenSilent')
		try {
			const silentResult = await this._clientApp.acquireTokenSilent({
				scopes: this._redirectRequest.scopes,
				account: this._accountInfo,
			})
			log('[DEBUG _handleSingleAccount] acquireTokenSilent succeeded')
			this._idToken = silentResult.idToken
			this._accessToken = silentResult.accessToken !== '' ? silentResult.accessToken : undefined
		} catch (e) {
			// Fall back to manual storage lookup if silent acquisition fails
			warn('[_handleSingleAccount] acquireTokenSilent failed, falling back to storage lookup:', e)
			this._setIdTokenFromStorage()
			this._setAccessTokenFromStorage()
		}

		// Re-check expiration after potential token refresh
		const finalTokenExpired = this._tokenExpired(expirationDate)
		if (finalTokenExpired) {
			log('[DEBUG _handleSingleAccount] Token still expired, calling _attemptAcquireTokenSilent')
			this._attemptAcquireTokenSilent(required)
		} else if (this._idToken) {
			log('[DEBUG _handleSingleAccount] Token valid, setting authenticated')
			this._setAuthenticated(expirationDate)
			this._handleUnverifiedUser()
		} else {
			warn('[_handleSingleAccount] Account present but no ID token; attempting silent refresh')
			this._attemptAcquireTokenSilent(required)
		}
	}

	private _attemptAcquireTokenSilent(required: boolean): void {
		this._clientApp
			.acquireTokenSilent({
				...this._redirectRequest,
				account: this._accountInfo,
			})
			.then(this._handleAuthResult.bind(this))
			.catch(error => {
				console.error('[_attemptAcquireTokenSilent] Error details:', {
					errorCode: error?.errorCode,
					errorMessage: error?.errorMessage,
					name: error?.name,
					subError: error?.subError,
					correlationId: error?.correlationId,
				})
				if (error instanceof InteractionRequiredAuthError) {
					// Refresh token is truly invalid - user must re-authenticate
					console.log('[_attemptAcquireTokenSilent] InteractionRequired - refresh token likely invalid')
					if (required) {
						this.loginRedirect(window.location.href)
					} else {
						// Just set unauthenticated state - don't tell B2C to invalidate all sessions
						this._setUnauthenticated()
					}
				} else {
					if (error instanceof BrowserAuthError) {
						console.log('[_attemptAcquireTokenSilent] BrowserAuthError:', error.errorCode)
					}
					// Transient error (network, etc.) - fail silently
					console.warn('Silent token refresh failed:', error)
					this._setUnauthenticated() // don't tell B2C to invalidate all sessions
				}
			})
	}

	/**
	 * Helper function to set auth state based on local storage information
	 * @private
	 */
	protected _checkAuthLocalStorage() {
		this._setUnauthenticated()
	}

	protected _setUnauthenticated() {
		log('[DEBUG _setUnauthenticated] Setting auth state to UNAUTHENTICATED')
		this._authState = AuthState.unAuthenticated
		dispatchGbpeAuthStateUpdated()
		this._dispatchAuthChecked()
	}

	private _dispatchAuthChecked() {
		window.dispatchEvent(new CustomEvent(AuthEvents.loaded))
	}

	/**
	 * Returns true if access/id token has expired, false if still valid.
	 */
	private _tokenExpired(timeToCheck: Date): boolean {
		//Here we assume that the local clock is being used when acquiring token
		//therefore, it is safe to use the local clock to check against token timestamp
		const now = Date.now()
		const difference = now - timeToCheck.getTime()
		return difference > 0
	}

	private _handleRedirectError(error: BrowserAuthError) {
		if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
			sessionStorage.removeItem(`msal.${this._clientId}.request.params`)
			sessionStorage.removeItem(`msal.${this._clientId}.interaction.status`)
		} else {
			console.error(error)
		}
	}
	protected _setIdTokenFromStorage() {
		// String Key to look for in storage
		const idTokenStorageKey =
			this._accountInfo.homeAccountId + '-' + this._accountInfo.environment + '-idtoken-' + this._clientId + '--'

		let storageIdToken

		// Check locale and decide whether to use session storage or local storage
		if (this._locale === 'ko-KR') {
			storageIdToken = JSON.parse(sessionStorage.getItem(idTokenStorageKey))?.secret
		} else {
			storageIdToken = JSON.parse(localStorage.getItem(idTokenStorageKey))?.secret
		}

		if (this._idToken == null && storageIdToken) {
			this._idToken = storageIdToken
		}
	}
	private _setAccessTokenFromStorage() {
		// String Key to look for in storage
		const accessTokenStorageKey =
			this._accountInfo.homeAccountId +
			'-' +
			this._accountInfo.environment +
			'-accesstoken-' +
			this._clientId +
			'--' +
			this._clientId

		let storageAccessToken

		// Check locale and decide whether to use session storage or local storage
		if (this._locale === 'ko-KR') {
			storageAccessToken = sessionStorage.getItem(accessTokenStorageKey)
				? JSON.parse(sessionStorage.getItem(accessTokenStorageKey)).secret
				: null
		} else {
			storageAccessToken = localStorage.getItem(accessTokenStorageKey)
				? JSON.parse(localStorage.getItem(accessTokenStorageKey)).secret
				: null
		}

		if (this._accessToken == null && storageAccessToken) {
			this._accessToken = storageAccessToken
		}
	}

	/**
	 * Redirect user to url specified in state member of Headless Auth Object
	 * In turn, determined by state parameter of the Authentication Request in Constructor.
	 * Only called after User inputs credentials and completes flow in Azure
	 * */
	protected _redirectToState(stateParam?: string): void {
		let finalURL
		if (stateParam && stateParam != '') {
			this._state = stateParam
		}
		//Absolute URL case
		if (this._state && isAbsoluteUrl(this._state)) {
			finalURL = new URL(this._state)
		} else {
			//relative url case
			finalURL = new URL(this._state, window.location.origin)
		}

		if (finalURL && finalURL != document.URL && finalURL.origin === window.location.origin) {
			//Using window location replace because we do not want to see the waypoint page when pressing back/forward
			window.location.replace(finalURL.href)
		} else if (!finalURL) {
			log('State Parameter from request does not exist')
		} else if (finalURL.origin !== window.location.origin) {
			log('The Final landing page is not on the same domain as the current page')
		} else {
			log('User has reached final landing page')
		}
	}

	/**
	 * Handle use case if the authenticated user's email is not verified
	 * @private
	 */
	private async _handleUnverifiedUser(): Promise<void> {
		//Check if the email is verified from the id token
		const emailVerifiedProperty = getVerificationStatusFromToken(this._idToken)
		const emailVerified =
			emailVerifiedProperty && emailVerifiedProperty.toLowerCase() === verificationConstants.verified
		//If the email is unverified and the locale for the user is for en-us
		//The user should be taken into the myAccount verification url
		//TODO: check up on Europe token, which does not have the verification status
		if (!emailVerified && 'SE' !== this._region.toUpperCase() && 'CH' !== this._region.toUpperCase()) {
			const redirectURL = await getRegionRedirect(
				this._idToken,
				this._locale,
				this._region,
				this._urlConfig.MyAccountProfileURL
			)
			redirectToVerificationPage(redirectURL)
		}
	}

	private _handleInactiveUser(): void {
		sessionStorage.setItem(InactivityStorageKeys.userSignedOut, 'true')
		this.logout()
	}

	private _handleStorageEvent(event: StorageEvent): void {
		//Handle event to logout
		if (event.key === AuthStorageKeys.userLogout && event.newValue !== null) {
			this.logout()
		} else {
			//Handle event to reset timer (i.e. user is active in another authenticated tab)
			if (event.key === AuthStorageKeys.userActive && event.newValue !== null) {
				this._timer.resetTimer()
			}
		}
	}

	private _emitLocalStorageLogout(): void {
		localStorage.setItem(AuthStorageKeys.userLogout, 'true')
		localStorage.removeItem(AuthStorageKeys.userLogout)
	}

	//Public Functions

	/**
	 * Login method set to use default login provided by MSAL
	 */
	loginRedirect(url?: string, extraParameters?: { [key: string]: string }, forceLogin?: boolean): void {
		if (this._authenticated) return

		const effectiveUrl = url?.trim() ? url : window.location.href
		let stateURL
		if (isAbsoluteUrl(effectiveUrl)) {
			stateURL = new URL(effectiveUrl)
		} else {
			stateURL = new URL(effectiveUrl, window.location.origin)
		}
		//If there is a predefined state url included and it's on the same domain as the current page, use that state url
		//Else use the headless auth object's redirect request state,
		//Finally just use the app's state if all else is undefined
		const stateParam =
			stateURL?.origin === window.location.origin
				? stateURL.href
				: this._redirectRequest?.state?.split('|')[0] ?? this._state

		//Need to update the redirect request state to be the final url for login redirect
		//Now adding locale to the state parameter
		if (stateURL?.origin === window.location.origin) {
			this._redirectRequest.state = stateURL.href + '|' + this._locale
		} else {
			if (stateURL) {
				log('URL included in login redirect function not on same domain')
			}
		}
		//Store the state parameter in sessionStorage for missingInfo Access
		if (stateParam) {
			sessionStorage.setItem(AuthStorageKeys.state, stateParam)
		}

		//If the login hint value is being populated, then by default we are using isNoo query parameter from My Account
		if (this._loginHint && this._loginHint !== '') {
			this._redirectRequest.extraQueryParameters.login_hint = this._loginHint
			this._redirectRequest.extraQueryParameters.isNoo = 'true'
		}

		//If there are extra parameters invoked with login method, merge them with the current default query params
		if (extraParameters) {
			const currentQueryParams = this._redirectRequest.extraQueryParameters
			this._redirectRequest.extraQueryParameters = {
				...currentQueryParams,
				...extraParameters,
			}
		}

		//If SSO has been opted out and denoted in sessionStorage, set the flag in the redirectRequest to prompt login
		if (forceLogin !== undefined && forceLogin === true) {
			sessionStorage.removeItem(AuthStorageKeys.promptLogin)
			this._redirectRequest.prompt = 'login'
		}

		//TODO: Check what happens if someone changes state parameter during sign in

		const authBaseUrl = getGmAccountsCdnEntraAuthBaseUrl()
		if (authBaseUrl) {
			const redirectUri = getRelayTokenWaypointAbsoluteUrl()
			const params = new URLSearchParams({
				client_id: this._clientId,
				redirect_uri: redirectUri,
				state: this._redirectRequest.state,
			})
			const target = `${authBaseUrl}?${params.toString()}`
			log(`[loginRedirect] Entra: navigating to GM accounts CDN (client_id=${this._clientId})`)
			window.location.assign(target)
			return
		}

		this._clientApp.loginRedirect(this._redirectRequest).catch(error => {
			this._handleRedirectError(error)
		})
	}

	/**
	 * logs the user out of the client application in the Headless Authentication Object
	 * @param url - optional parameter denoting where the user will redirect to after signing out
	 */
	logout(url?: string): void {
		// DEBUG: Track when logout is called - this will invalidate B2C session!
		warn(`[DEBUG logout] LOGOUT CALLED! url=${url}, stack=${new Error().stack}`)

		//Clearing items related to server time
		sessionStorage.removeItem(AuthStorageKeys.serverOffset)
		sessionStorage.removeItem(InactivityStorageKeys.customTimeOut)

		//Clear User data from storage
		clearUserInfo(this._urlConfig)

		//clear cookie API request
		this.clearCookie()

		//Remove event listeners
		window.removeEventListener(InactivityEvents.inactiveUser, this._boundInactiveUserHandler)
		window.removeEventListener('storage', this._boundStorageHandler)

		//Emit local storage event
		this._emitLocalStorageLogout()

		//If the url parameter is present and it is on the same domain as the current page, use the url parameter
		//Use case: if the url is relative or if the url is absolute but on the same page
		//Else, same domain is false
		const isSameDomain = url ? !isAbsoluteUrl(url) || new URL(url).origin === window.location.origin : false
		let postLogoutRedirectTarget: string
		if (url && isSameDomain) {
			postLogoutRedirectTarget = isAbsoluteUrl(url)
				? new URL(url).href
				: new URL(url, window.location.origin).href
		} else {
			postLogoutRedirectTarget = this._postLogoutRedirectUri
		}
		const absolutePostLogoutRedirectUri = isAbsoluteUrl(postLogoutRedirectTarget)
			? postLogoutRedirectTarget
			: new URL(postLogoutRedirectTarget, window.location.origin).href

		const logoutBaseUrl = getGmAccountsCdnEntraLogoutBaseUrl()
		if (logoutBaseUrl) {
			const params = new URLSearchParams({
				post_logout_redirect_uri: absolutePostLogoutRedirectUri,
			})
			const target = `${logoutBaseUrl}?${params.toString()}`
			log(`[logout] Entra: navigating to GM accounts CDN logout (post_logout_redirect_uri set)`)
			window.location.assign(target)
			return
		}

		if (url && isSameDomain) {
			const logoutRequest: EndSessionRequest = {
				postLogoutRedirectUri: postLogoutRedirectTarget,
			}
			this._clientApp.logout(logoutRequest).catch(console.error)
		} else {
			this._clientApp.logout().catch(console.error)
		}
	}

	/**
	 *  clear cookie API
	 */
	async clearCookie() {
		try {
			const response = await fetch(API_LOGOUT_CLEAR_COOKIE_PATH)
			const responseStatus = response.status
			if (responseStatus === 200) {
				log('[clearCookie] Cookie cleared successfully')
			} else if (responseStatus === 404) {
				warn(
					'[clearCookie] Endpoint not found (404) - cookie clearing may not be configured for this environment'
				)
			} else {
				warn(`[clearCookie] Unexpected response status: ${responseStatus}`)
			}
		} catch (err) {
			warn('[clearCookie] Network error - unable to clear cookie:', err)
		}
	}

	/**
	 * Variation of login method that instead uses the sign up request
	 * @param accountRegisterURL - the my Account URL for creating an account
	 * @param stateUrl - the optional url used as the state parameter that is used as the final page user redirects to
	 */
	registerRedirect(accountRegisterURL?: string, stateUrl?: string): void {
		//First copy the signUpRequest object to a new constant
		const accountRegisterRequest = Object.assign({}, this._signupRequest)
		//Overrides on redirect navigate callback of redirectRequest msal object to custom handler
		accountRegisterRequest.onRedirectNavigate = this._registerNavigateHandler.bind(this)

		//accountRegisterURL denotes the path to the my Account create account page on the domain
		//Need to grab only the origin and pathname of the Register Account URL
		if (accountRegisterURL) {
			if (isAbsoluteUrl(accountRegisterURL)) {
				const fullCreateAccountURL = new URL(accountRegisterURL)
				this._createAccountURL = fullCreateAccountURL.origin + fullCreateAccountURL.pathname
			} else {
				const fullCreateAccountURL = new URL(accountRegisterURL, window.location.origin)
				this._createAccountURL = fullCreateAccountURL.origin + fullCreateAccountURL.pathname
			}
		}
		//url optional parameter denotes the state parameter (used here as the final page user should land on in register flow)
		if (stateUrl) {
			if (isAbsoluteUrl(stateUrl)) {
				accountRegisterRequest.state = stateUrl
			} else {
				accountRegisterRequest.state = new URL(stateUrl, window.location.origin).href
			}
		}
		this._clientApp
			.loginRedirect(accountRegisterRequest)
			.then(this._registerNavigateCallback.bind(this))
			.catch(error => {
				this._handleRedirectError(error)
			})
	}

	private _registerNavigateHandler(url: string): boolean {
		const azureQueryParams = new URL(url).search
		const fullAccountURLPath = new URL(this._createAccountURL + azureQueryParams)
		this._myAccountURL = fullAccountURLPath.href
		return false
	}

	private _registerNavigateCallback(): void {
		window.location.href = this._myAccountURL
	}

	/**
	 * Get the IdToken if it exists (returns cached value, may be expired)
	 */
	get idToken(): string | null {
		return this._idToken
	}

	/**
	 * Get the AccessToken if it exists (returns cached value, may be expired)
	 */
	get accessToken(): string | null {
		return this._accessToken
	}

	/**
	 * Get valid tokens, refreshing silently if necessary.
	 * Use this method when you need guaranteed valid tokens for API calls.
	 * Returns null if user is not authenticated or refresh fails.
	 */
	async getValidTokens(): Promise<{ idToken: string; accessToken?: string } | null> {
		if (!this._accountInfo) {
			return null
		}

		try {
			const result = await this._clientApp.acquireTokenSilent({
				scopes: this._redirectRequest.scopes,
				account: this._accountInfo,
			})
			this._idToken = result.idToken
			this._accessToken = result.accessToken !== '' ? result.accessToken : undefined
			return {
				idToken: result.idToken,
				accessToken: this._accessToken,
			}
		} catch (err) {
			warn('[getValidTokens] Token refresh failed:', err)
			return null
		}
	}

	get missingInfoRequired(): string | null {
		return this._redirectRequest.extraQueryParameters.requiredMissingInfo
	}

	get authState(): number {
		return this._authState
	}

	setInactiveTimeOut(timeout: number): void {
		const inactiveTime = timeout.toString()
		sessionStorage.setItem(InactivityStorageKeys.customTimeOut, inactiveTime)
		window.location.reload()
	}
}
